import { useForm } from '@tanstack/react-form'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { usePrimaryListing, useUpdateListing } from '~/entities/listing/model/use-listings'
import {
  BasicInfoSection,
  ContactInfoSection,
  LocationSection,
  SocialLinksSection,
} from '~/features/business/components'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Button } from '~/shared/ui/button'
import { Separator } from '~/shared/ui/separator'
import { Skeleton } from '~/shared/ui/skeleton'
import { PageHeader } from '~/widgets/page-header'

export const Route = createFileRoute('/dashboard/business/edit')({
  component: EditBusinessProfile,
})

/**
 * "Edit business" is structurally an "edit primary listing" page - every
 * field on every form here (name, description, hours, location, social
 * links) lives on a Listing, not on the Business record. The hook split
 * (useCurrentBusiness for `id`, usePrimaryListing for the editable fields,
 * useUpdateListing for the PUT) makes that explicit so the next reader
 * doesn't have to chase the previous Business=Listing alias to figure out
 * why editing "business name" was hitting `PUT /listings/:id`.
 */
function EditBusinessProfile() {
  const navigate = useNavigate()
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: listing, isLoading } = usePrimaryListing(businessId)
  const updateListing = useUpdateListing(businessId)

  const basicInfoForm = useForm({
    defaultValues: {
      name: listing?.name || '',
      description: listing?.description || '',
    },
    onSubmit: async ({ value }) => {
      if (!listing) return
      try {
        await updateListing.mutateAsync({
          listingId: listing.id,
          data: value,
        })
        toast.success(m.businessInformationUpdated(), {
          description: m.yourChangesHaveBeenSavedSuccessfully(),
        })
        navigate({ to: '/dashboard/business' })
      } catch (error) {
        toast.error(getErrorMessageFromError(error))
      }
    },
  })

  const contactForm = useForm({
    defaultValues: {
      phone: listing?.phone || '',
      email: listing?.email || '',
      website: listing?.website || '',
    },
    onSubmit: async ({ value }) => {
      if (!listing) return
      try {
        await updateListing.mutateAsync({
          listingId: listing.id,
          data: value,
        })
        toast.success(m.contactInformationUpdated(), {
          description: m.yourChangesHaveBeenSavedSuccessfully(),
        })
      } catch (error) {
        toast.error(getErrorMessageFromError(error))
      }
    },
  })

  const locationForm = useForm({
    defaultValues: {
      street: listing?.location.street || '',
      city: listing?.location.city || '',
      country: listing?.location.country || 'Austria',
    },
    onSubmit: async ({ value }) => {
      if (!listing) return
      try {
        await updateListing.mutateAsync({
          listingId: listing.id,
          data: { location: value },
        })
        toast.success(m.locationUpdated(), {
          description: m.yourChangesHaveBeenSavedSuccessfully(),
        })
      } catch (error) {
        toast.error(getErrorMessageFromError(error))
      }
    },
  })

  const socialLinksForm = useForm({
    defaultValues: {
      facebook: listing?.facebook || '',
      instagram: listing?.instagram || '',
      twitter: listing?.twitter || '',
    },
    onSubmit: async ({ value }) => {
      if (!listing) return
      try {
        await updateListing.mutateAsync({
          listingId: listing.id,
          data: value,
        })
        toast.success(m.socialMediaLinksUpdated(), {
          description: m.yourChangesHaveBeenSavedSuccessfully(),
        })
      } catch (error) {
        toast.error(getErrorMessageFromError(error))
      }
    },
  })

  if (isLoading) {
    return <EditBusinessProfileSkeleton />
  }

  if (!listing) {
    // No primary listing exists yet. The "edit business" forms have nothing
    // to edit - point the user at the listing-creation flow instead of the
    // older "no business found" dead-end.
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <h2 className="text-2xl font-semibold mb-2">{m.noListingsYet()}</h2>
        <p className="text-muted-foreground mb-4">
          {m.createYourFirstListingToShowcaseYourBusiness()}
        </p>
        <Link to="/setup/profile" search={{ kind: 'place' }}>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            {m.createListing()}
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={m.editBusinessProfile()}
        description={m.updateYourBusinessInformationAndSettings()}
        action={
          <Button variant="outline" onClick={() => navigate({ to: '/dashboard/business' })}>
            {m.cancel()}
          </Button>
        }
      />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          basicInfoForm.handleSubmit()
        }}
      >
        <BasicInfoSection form={basicInfoForm} />
        <Button type="submit" disabled={updateListing.isPending} className="mt-4">
          {updateListing.isPending ? m.saving() : m.saveBasicInformation()}
        </Button>
      </form>
      <Separator />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          contactForm.handleSubmit()
        }}
      >
        <ContactInfoSection form={contactForm} />
        <Button type="submit" disabled={updateListing.isPending} className="mt-4">
          {updateListing.isPending ? m.saving() : m.saveContactInformation()}
        </Button>
      </form>
      <Separator />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          locationForm.handleSubmit()
        }}
      >
        <LocationSection form={locationForm} />
        <Button type="submit" disabled={updateListing.isPending} className="mt-4">
          {updateListing.isPending ? m.saving() : m.saveLocation()}
        </Button>
      </form>
      <Separator />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          socialLinksForm.handleSubmit()
        }}
      >
        <SocialLinksSection form={socialLinksForm} />
        <Button type="submit" disabled={updateListing.isPending} className="mt-4">
          {updateListing.isPending ? m.saving() : m.saveSocialMediaLinks()}
        </Button>
      </form>
    </div>
  )
}

function EditBusinessProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Skeleton className="h-8 w-64 max-w-full mb-2" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <Skeleton className="h-10 w-24" />
      </div>

      <Skeleton className="h-96" />
      <Separator />
      <Skeleton className="h-64" />
    </div>
  )
}
